import { useCallback, useState } from 'react';
import type { SubmitHandler } from 'react-hook-form';
import { generatePath, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { ApiPatchOperation } from '@domain/schemas/api-patch-operation';
import { patchEntity } from '@mutations/patchEntity';
import { entityQueries } from '@queries/fetchEntity';
import { invalidateSavedViewCounts } from '@queries/fetchSavedViews';
import Nav from '@src/routes';
import { useQueryClient } from '@tanstack/react-query';
import { isPPLFrontend } from '@utils/app-paths';

import type { CreateContractFormState } from '../types';

export const useCreateContract = (id: string) => {
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const handleOpenEntity = useCallback(
    (id: string) => {
      const payload = { id };
      const newPath = Nav.SubmissionContract;
      const nextRoute = generatePath(newPath, payload);

      return navigate(nextRoute, { replace: true });
    },
    [navigate],
  );

  const handleUpdateContract: SubmitHandler<CreateContractFormState> = useCallback(
    async (data) => {
      try {
        setIsLoading(true);
        const { branding, branding_id, class_of_business, copy_from_submission, copy_from_template, name, team } = data;
        const editedDetails = isPPLFrontend ? {} : { name, teams: team };

        await patchEntity({
          operation: ApiPatchOperation.PreDraftToDraft,
          branding,
          branding_id,
          class_of_business,
          copy_from_submission,
          copy_from_template,
          ...editedDetails,
        });

        await queryClient.invalidateQueries({ queryKey: entityQueries.detail(id).queryKey });
        await invalidateSavedViewCounts(queryClient);

        return handleOpenEntity(id);
      } catch (_error) {
        toast.error('Failed to create the contract. Please try again.');
      } finally {
        setIsLoading(false);
      }
    },
    [handleOpenEntity, id, queryClient],
  );

  return {
    isLoading,
    onSubmit: handleUpdateContract,
  };
};
